// Same API contract as /app/mobile/src/api.ts (Bearer token + refresh token).
export const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCESS_KEY = "auth_token";
const REFRESH_KEY = "refresh_token";

let access = null;
let refreshTok = null;
let onUnauthorized = null;

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

export function loadTokens() {
  try {
    access = localStorage.getItem(ACCESS_KEY);
    refreshTok = localStorage.getItem(REFRESH_KEY);
  } catch {
    access = null;
  }
  return !!access;
}

export function saveTokens(a, r) {
  access = a;
  refreshTok = r;
  try {
    localStorage.setItem(ACCESS_KEY, a);
    localStorage.setItem(REFRESH_KEY, r);
  } catch {}
}

export function clearTokens() {
  access = null;
  refreshTok = null;
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {}
}

function translate(msg) {
  if (msg.includes("valid email")) return "adresse e-mail invalide";
  if (msg.includes("at least")) return "valeur trop courte";
  if (msg.includes("Field required")) return "champ obligatoire";
  return msg;
}

export function formatError(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        const field = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : "";
        return e?.msg ? `${field ? field + " : " : ""}${translate(e.msg)}` : "";
      })
      .filter(Boolean)
      .join("\n");
  }
  if (typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function safeJson(t) {
  try {
    return JSON.parse(t);
  } catch {
    return { detail: t };
  }
}

async function tryRefresh() {
  if (!refreshTok) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshTok }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    saveTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

export async function api(path, opts = {}, retried = false) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? "GET",
      headers: {
        "Content-Type": "application/json",
        ...(access ? { Authorization: `Bearer ${access}` } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    throw new ApiError("Connexion impossible. Vérifiez votre réseau.", 0);
  }
  if (res.status === 401 && access && !retried && !path.startsWith("/auth/login")) {
    if (await tryRefresh()) return api(path, opts, true);
    clearTokens();
    onUnauthorized?.();
  }
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) throw new ApiError(formatError(data?.detail), res.status);
  return data;
}

// Upload a browser File (from <input type="file">) to POST /api/files.
export async function uploadFile(file, purpose = "document") {
  await api("/auth/me"); // refresh token if needed
  const form = new FormData();
  form.append("purpose", purpose);
  form.append("file", file, file.name);
  let res;
  try {
    res = await fetch(`${API_BASE}/files`, {
      method: "POST",
      body: form,
      headers: access ? { Authorization: `Bearer ${access}` } : undefined,
    });
  } catch {
    throw new ApiError("Connexion impossible. Vérifiez votre réseau.", 0);
  }
  const data = safeJson(await res.text());
  if (!res.ok) throw new ApiError(formatError(data?.detail), res.status);
  return data;
}

export function authedUrl(path) {
  const sep = path.includes("?") ? "&" : "?";
  return `${API_BASE}${path}${sep}auth=${encodeURIComponent(access ?? "")}`;
}

// Downloads a protected attachment (CSV / JSON export) as a file, without leaving a blank tab.
export async function downloadProtected(path, fallbackName = "export") {
  await api("/auth/me");
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, { headers: access ? { Authorization: `Bearer ${access}` } : undefined });
  } catch {
    throw new ApiError("Connexion impossible. Vérifiez votre réseau.", 0);
  }
  if (!res.ok) {
    const data = safeJson(await res.text());
    throw new ApiError(formatError(data?.detail), res.status);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const name = /filename="?([^";]+)"?/.exec(cd)?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Opens a protected file/export in a new tab (download or viewer).
export async function openProtected(path) {
  const win = window.open("", "_blank");
  try {
    await api("/auth/me");
  } catch (e) {
    win?.close();
    throw e;
  }
  const url = authedUrl(path);
  if (win) win.location.href = url;
  else window.open(url, "_blank");
}
